import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  OnInit,
  output,
  signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  ControlContainer,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NgControl,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { isArray, isEqual } from 'lodash';
import { FormGroupComponent } from 'mbs-ui-kit';
import { SelectComponent } from 'mbs-ui-kit/form/select/select.component';
import { PermissionsEnum } from 'mbs-ui-shared@models';
import { combineLatest, merge, Observable, of, Subject } from 'rxjs';
import { filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'mbs-paged-select',
  templateUrl: './paged-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgbDropdownModule, FormGroupComponent, SelectComponent]
})
export class PagedSelectComponent<T> implements ControlValueAccessor, OnInit, AfterViewInit {
  controlContainer = inject(ControlContainer, { optional: true, host: true, skipSelf: true });
  protected ngControl = inject(NgControl, { optional: true, self: true });
  protected fb = inject(FormBuilder);

  public typeahead$: Subject<string> = new Subject<string>();

  public value = model<T | T[]>();
  public disabled = model<boolean>(false);
  public multiple = input<boolean>(false);
  public counterLabel = input<string>(null);
  public formControlName = input<string>(null);
  public selectedId = model<string>(null);
  public selectedIds = model<string[]>([]);
  public appendTo = input<string>(null);
  public clearable = input<boolean>(true);
  public searchable = input<boolean>(true);
  public showAddNewButton = input<boolean>(false);
  public e2eLocators = input<Record<string, string>>(null);
  public placeholder = input<string>('');
  public label = input<string | TemplateRef<any>>(null);
  public showRequiredMark = input<boolean>(false);

  public closeEvent = output<void>();
  public clearEvent = output<void>();
  public selectionChange = output<T | T[]>();

  protected pagedSelect = viewChild(SelectComponent);

  protected readonly validators = Validators;
  protected readonly permissionsEnum = PermissionsEnum;

  protected bindLabel = 'name';
  protected loading = signal<boolean>(false);
  protected items = signal<T[]>([]);
  protected control: FormControl<T | T[]>;

  private combinedSelectedIds = computed(() => {
    return this.multiple() ? this.selectedIds() : this.selectedId() ? [this.selectedId()] : [];
  });
  private selectedIds$ = toObservable(this.combinedSelectedIds);
  private proposedValues: T | T[];
  private fetchData = new Subject<void>();

  constructor() {
    const ngControl = this.ngControl;

    this.ngControl = ngControl || ({} as any);

    if (ngControl) {
      this.ngControl.valueAccessor = this;
    }

    effect(() => {
      const myValue = this.value();
      this.onChange?.(myValue);
      this.selectionChange.emit(myValue);
    });
  }

  ngOnInit() {
    if (this.controlContainer) {
      if (this.formControlName) {
        this.control = this.controlContainer.control.get(this.formControlName()) as FormControl;
      } else {
        console.warn('Missing formControlName directive');
      }
    } else {
      this.control = this.ngControl.control as FormControl;
    }
  }

  ngAfterViewInit(): void {
    this.initStreams();
  }

  protected loadData(): void {
    //
  }

  protected get getData$(): Observable<T[]> {
    return of([]);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected loadByIds(ids: string[]): void {
    //
  }

  protected get getTotal$(): Observable<number> {
    return of(0);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected getByIds$(ids: string[]): Observable<T[]> {
    return of(null);
  }

  protected get allLoaded$(): Observable<boolean> {
    return of(false);
  }

  protected get dataHasError$(): Observable<boolean> {
    return of(false);
  }

  protected get loadingData$(): Observable<boolean> {
    return of(false);
  }

  protected get loadingById$(): Observable<boolean> {
    return of(false);
  }

  protected areItemsTheSame(selectedItem: T, item: T): boolean {
    return false;
  }

  public open() {
    this.pagedSelect()?.ngSelect.open();
  }

  public close() {
    this.pagedSelect()?.ngSelect.close();
  }

  private initStreams() {
    combineLatest([this.loadingData$, this.loadingById$])
      .pipe(map(([loadingData, loadingById]) => loadingData || loadingById))
      .pipe(untilDestroyed(this))
      .subscribe((loading) => this.loading.set(loading));

    const selectScrollToEnd$ = this.pagedSelect().scrollToEnd;
    const selectScroll$ = this.pagedSelect().scroll.pipe(filter(({ end }) => end + 10 >= this.items.length));

    merge(selectScrollToEnd$, selectScroll$)
      .pipe(untilDestroyed(this))
      .subscribe(() => this.fetchData.next());

    this.fetchData
      .pipe(
        withLatestFrom(this.allLoaded$, this.dataHasError$),
        filter(([, loaded, hasError]) => !loaded && !hasError),
        switchMap(() =>
          this.loadingData$.pipe(
            filter((loading) => !loading),
            take(1)
          )
        ),
        untilDestroyed(this)
      )
      .subscribe(() => this.loadData());

    this.selectedIds$
      .pipe(
        filter((selectedIds) => !!selectedIds?.length && this.items.length === 0),
        untilDestroyed(this)
      )
      .subscribe((selectedIds) => {
        this.loadByIds(selectedIds);
        this.fetchData.next();
      });

    const loadedSelectedItems$ = this.selectedIds$.pipe(
      switchMap((selectedIds) =>
        selectedIds?.length
          ? this.getByIds$(selectedIds).pipe(
              filter((items) => !!items.filter(Boolean).length),
              take(1)
            )
          : of(null)
      )
    );

    loadedSelectedItems$
      .pipe(filter(Boolean), untilDestroyed(this))
      .subscribe((selectedItems) => this.control.patchValue(this.multiple() ? selectedItems : selectedItems[0]));

    combineLatest([this.getData$, loadedSelectedItems$])
      .pipe(
        map(([items, selectedItems]) => {
          return selectedItems?.length
            ? (selectedItems as T[]).concat(
                items.filter((item) => !selectedItems.some((selectedItem) => this.areItemsTheSame(selectedItem, item)))
              )
            : items;
        }),
        untilDestroyed(this)
      )
      .subscribe((items) => this.items.set(items));
  }

  onChange: (value: T | T[]) => void;
  onTouched: () => void;

  writeValue(val: T | T[]) {
    if (this.multiple() && !isArray(val)) return;
    if (!this.multiple() && !this.proposedValues && !val) return;
    if (!isArray(val) && !isArray(this.proposedValues) && this.areItemsTheSame(this.proposedValues, val)) return;
    if (isEqual(this.proposedValues, val)) return;

    this.proposedValues = val;
    this.value.set(this.proposedValues);
  }
  registerOnChange(fn: (value) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
